import React, { useLayoutEffect, useMemo } from "react";
import { SelectYear } from "./SelectYear";
import { CalendarContainer, calendarContainerWithFooter } from "./CalendarContainer";
import ReactDatePicker, { ReactDatePickerCustomHeaderProps, ReactDatePickerProps } from "react-datepicker";
import { DateTextInput } from "./DateTextInput";
import { DatePickerGranularity } from "./types";
import "./yearMonthOrDatePicker.css";
import clsx from "clsx";
import { format } from "date-fns";
import { Icon } from "../Icon";

export type YearMonthOrDayPickerProps = Omit<ReactDatePickerProps, "isClearable"> & {
  className?: string;
  renderInPortal?: boolean;
  granularity: DatePickerGranularity;
  renderFooter?: React.ComponentType;
  onChange: (date: Date | null | undefined) => void;
  minDate?: Date | null;
  maxDate?: Date;
  testId?: string;
  showMask?: boolean;
};

export function YearMonthOrDayPicker({
  className,
  renderInPortal,
  granularity,
  onChange,
  renderFooter,
  selected,
  minDate,
  maxDate,
  testId,
  disabled,
  placeholderText,
  showMask,
  ...props
}: YearMonthOrDayPickerProps) {
  const NUMBER_OF_YEARS_TO_SHOW = 9;

  const element = useMemo(() => {
    const el = document.createElement("div");
    el.style.cssText = "position: absolute; top: 0; left: 0; z-index: 1000;";
    el.id = `year-month-or-date-picker-${Math.floor(Math.random() * 100000)}`;
    el.className = clsx(`year-month-or-date-picker year-month-or-date-picker__${granularity} flex flex-col`, className);
    return el;
  }, []);

  useLayoutEffect(() => {
    if (renderInPortal) {
      document.body.appendChild(element);
      return () => {
        document.body.removeChild(element);
      };
    }
  }, [renderInPortal, element]);

  const handleChangeDate = (date: Date | null | undefined) => {
    onChange(date);
  };

  const renderCustomHeader = useMemo(() => {
    switch (granularity) {
      case "month":
        return function CustomDatePickerHeader(customHeaderProps: ReactDatePickerCustomHeaderProps) {
          return (
            <SelectYear
              {...customHeaderProps}
              selectedDate={selected || new Date()}
              onChange={handleChangeDate}
              maxYear={maxDate?.getFullYear()}
              minYear={minDate?.getFullYear()}
            />
          );
        };
      case "day":
        return CustomHeaderDay;
      default:
        return undefined;
    }
  }, [granularity, selected, maxDate, minDate, handleChangeDate]);

  return (
    <div className={`year-month-or-date-picker year-month-or-date-picker__${granularity} flex flex-col`}>
      <ReactDatePicker
        showIcon
        endDate={new Date()}
        selected={selected}
        icon={<i className="right-0 flex icon-lt-calendar-line" />}
        portalId={renderInPortal ? element.id : undefined}
        strictParsing
        calendarContainer={renderFooter ? calendarContainerWithFooter(renderFooter) : CalendarContainer}
        onChange={(date, event) => {
          onChange(date, event);
        }}
        showPopperArrow={false}
        clearButtonClassName="mr-6"
        previousYearButtonLabel={<i className="icon-lt-arrow-left-s-line" />}
        nextYearButtonLabel={<i className="icon-lt-arrow-right-s-line" />}
        renderCustomHeader={renderCustomHeader}
        yearItemNumber={NUMBER_OF_YEARS_TO_SHOW}
        customInput={
          <div>
            <DateTextInput
              testId={`date-picker${testId ? "-" + testId : ""}`}
              disabled={disabled}
              granularity={granularity}
              onDateChange={handleChangeDate}
              selectedDate={selected}
              placeholder={placeholderText}
              showMask={showMask}
            />
          </div>
        }
        {...granularityBasedProps[granularity]}
        minDate={minDate}
        maxDate={maxDate}
        {...props}
        //we need to set it to false as we use a custom textfield
        isClearable={false}
      />
    </div>
  );
}
type DateGranularityBasedProps = Partial<
  Pick<
    ReactDatePickerProps,
    | "dateFormat"
    | "calendarClassName"
    | "showMonthYearPicker"
    | "showYearPicker"
    | "showFullMonthYearPicker"
    | "renderDayContents"
    | "renderMonthContent"
    | "renderCustomHeader"
  >
>;

const granularityBasedProps: Record<DatePickerGranularity, DateGranularityBasedProps> = {
  year: {
    dateFormat: "yyyy",
    showYearPicker: true,
  },
  month: {
    dateFormat: "MM/yyyy",
    showMonthYearPicker: true,
  },
  day: {
    dateFormat: "dd/MM/yyyy",
    showFullMonthYearPicker: true,
  },
};

function CustomHeaderDay(props: ReactDatePickerCustomHeaderProps) {
  return (
    <div className="flex justify-between items-center">
      <button
        onClick={props.decreaseYear}
        disabled={props.prevYearButtonDisabled}
        className="flex items-center cursor-pointer hover:bg-blue-grey-100 rounded-sm"
        title="Previous Year"
      >
        <Icon icon="icon-lt-arrow-left-double-line" size="lg" className="cursor-pointer text-platform-blue-950" />
      </button>
      <button
        onClick={props.decreaseMonth}
        disabled={props.prevMonthButtonDisabled}
        className="flex items-center cursor-pointer hover:bg-blue-grey-100 rounded-sm"
        title="Previous Month"
      >
        <Icon icon="icon-lt-arrow-left-s-line" size="lg" className="cursor-pointer text-platform-blue-950" />
      </button>

      <span className="text-sm font-bold w-full text-center select-none">{format(props.monthDate, "MMMM yyyy")}</span>

      <button
        onClick={props.increaseMonth}
        disabled={props.nextMonthButtonDisabled}
        className="flex items-center cursor-pointer hover:bg-blue-grey-100 rounded-sm"
        title="Next Month"
      >
        <Icon icon="icon-lt-arrow-right-s-line" size="lg" className="cursor-pointer text-platform-blue-950" />
      </button>
      <button
        onClick={props.increaseYear}
        disabled={props.nextYearButtonDisabled}
        className="flex items-center cursor-pointer hover:bg-blue-grey-100 rounded-sm"
        title="Next Year"
      >
        <Icon icon="icon-lt-arrow-right-double-line" size="lg" className="cursor-pointer text-platform-blue-950" />
      </button>
    </div>
  );
}
